/**
 * ReplySignatureCard - dedicated editor for the
 * ``reply.signature_template`` system_settings row.
 *
 * Lives on the AdminSettingsPage above the generic row editor. Unlike
 * the generic JSON editor, this card:
 *   - Renders the template in a tall plain-text textarea (the value is
 *     plain text, not JSON, so admins don't need quotes around it).
 *   - Shows a live HTML preview built by the same
 *     ``renderReplySignatureHtml`` helper the agent composer uses, so
 *     the admin sees exactly what an agent will see.
 *   - The preview substitutes a sample first name so the
 *     ``{first_name}`` placeholder is visible-as-rendered.
 *
 * Saves through the existing ``PUT /admin/settings/{key}`` endpoint -
 * no new backend route. is_secret=false, category="agent-composer".
 */
import { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Button,
  Card,
  Spinner,
  Textarea,
} from "@/components/ui";
import {
  ADMIN_SETTINGS_QUERY_KEY,
  useAdminSettings,
  useUpsertSetting,
} from "@/hooks/useAdminSettings";
import {
  REPLY_SIGNATURE_QUERY_KEY,
  renderReplySignatureHtml,
} from "@/hooks/useReplySignature";
import { sanitizeHtml } from "@/lib/sanitize";
import { useQueryClient } from "@tanstack/react-query";

const SETTING_KEY = "reply.signature_template";
const SETTING_CATEGORY = "agent-composer";
const SETTING_DESCRIPTION =
  "Plain-text template that pre-fills the agent reply composer. "
  + "The placeholder {first_name} is substituted with the requester "
  + "first name at render time.";

const PREVIEW_FIRST_NAME = "Alex";

const FALLBACK_TEMPLATE =
  "Hi {first_name},\n\n\n\nRegards,\n\nBuyist Support\n"
  + "https://buyist.com\nsupport@buyist.com\n\n"
  + "Note: our office hours are from 8am to 5pm, "
  + "Monday through Friday EST.\n";

export function ReplySignatureCard(): JSX.Element {
  // Pull the value from the admin settings list so we share the cache
  // with the generic row editor below. The list response masks
  // is_secret=true rows but this row is non-secret, so the value
  // arrives intact.
  const settingsQuery = useAdminSettings();
  const upsert = useUpsertSetting();
  const queryClient = useQueryClient();

  const currentTemplate = useMemo<string>(() => {
    const row = (settingsQuery.data ?? []).find(
      (r) => r.key === SETTING_KEY,
    );
    if (!row) return FALLBACK_TEMPLATE;
    if (typeof row.value === "string") return row.value;
    // Defensive: if some earlier admin edited the row via the generic
    // editor and stored a non-string JSON value, surface the fallback
    // rather than try to coerce something unexpected.
    return FALLBACK_TEMPLATE;
  }, [settingsQuery.data]);

  const [draft, setDraft] = useState<string>("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState<boolean>(false);

  // Seed the textarea once the query lands. We deliberately do NOT
  // reset draft on subsequent refetches - the admin could be mid-edit
  // and another tab's invalidation must not wipe their work.
  const [seeded, setSeeded] = useState<boolean>(false);
  useEffect(() => {
    if (!seeded && settingsQuery.isSuccess) {
      setDraft(currentTemplate);
      setSeeded(true);
    }
  }, [seeded, settingsQuery.isSuccess, currentTemplate]);

  const isDirty = seeded && draft !== currentTemplate;

  const previewHtml = useMemo(() => {
    const html = renderReplySignatureHtml(draft, PREVIEW_FIRST_NAME);
    // The preview is admin-controlled content - we trust it, but we
    // still pass it through DOMPurify so the preview pane is a faithful
    // representation of what an end-user's mail client will see.
    return sanitizeHtml(html);
  }, [draft]);

  const handleSave = async () => {
    setSaveError(null);
    setSaveOk(false);
    try {
      await upsert.mutateAsync({
        key: SETTING_KEY,
        body: {
          value: draft,
          is_secret: false,
          description: SETTING_DESCRIPTION,
          category: SETTING_CATEGORY,
        },
      });
      // Invalidate the agent-facing copy of the template so the next
      // composer mount picks up the new value without a hard reload.
      await queryClient.invalidateQueries({
        queryKey: REPLY_SIGNATURE_QUERY_KEY,
      });
      // useUpsertSetting already invalidates ADMIN_SETTINGS_QUERY_KEY
      // on success; we duplicate here so a manual save without state
      // change in onSuccess still picks up. Harmless if already done.
      await queryClient.invalidateQueries({
        queryKey: ADMIN_SETTINGS_QUERY_KEY,
      });
      setSaveOk(true);
      // Clear the "saved" indicator after a couple seconds.
      window.setTimeout(() => setSaveOk(false), 2500);
    } catch (err) {
      const detail = (err as {
        response?: { data?: { detail?: string } };
      })?.response?.data?.detail;
      setSaveError(
        typeof detail === "string"
          ? detail
          : "Could not save the reply signature. Try again.",
      );
    }
  };

  const handleResetToServer = () => {
    setDraft(currentTemplate);
    setSaveError(null);
    setSaveOk(false);
  };

  const handleResetToDefault = () => {
    setDraft(FALLBACK_TEMPLATE);
    setSaveError(null);
    setSaveOk(false);
  };

  return (
    <section className="space-y-4" aria-label="Reply signature">
      <div>
        <h2 className="text-lg font-semibold text-neutral-900">
          Reply signature
        </h2>
        <p className="mt-1 text-sm text-neutral-600">
          Prefills the agent reply composer with a greeting + signature
          block. Use{" "}
          <code className="rounded bg-neutral-100 px-1 py-0.5 font-mono text-xs">
            {"{first_name}"}
          </code>{" "}
          to insert the requester's first name. The template is plain
          text - bare URLs and email addresses become clickable links
          automatically when sent.
        </p>
      </div>

      <Card padding="md">
        {settingsQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-3">
              <Textarea
                id="reply-signature-template"
                label="Template"
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setSaveOk(false);
                }}
                rows={14}
                spellCheck
                helperText={`Sample preview uses "${PREVIEW_FIRST_NAME}" for {first_name}.`}
                style={{ fontFamily: "monospace" }}
              />
              {saveError && (
                <Alert variant="error" title="Could not save">
                  {saveError}
                </Alert>
              )}
              {saveOk && (
                <Alert variant="success">Reply signature saved.</Alert>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => {
                    void handleSave();
                  }}
                  isLoading={upsert.isPending}
                  disabled={!isDirty || upsert.isPending}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResetToServer}
                  disabled={!isDirty || upsert.isPending}
                >
                  Discard changes
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleResetToDefault}
                  disabled={upsert.isPending}
                >
                  Reset to default
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <span className="block text-sm font-medium text-neutral-700">
                Preview
              </span>
              <div
                className="prose prose-sm max-w-none rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-900"
                aria-label="Reply signature preview"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </div>
        )}
      </Card>
    </section>
  );
}

export default ReplySignatureCard;
